/**
 * <CombatLogRibbon /> — replaces the old inline combat-log block.
 *
 * Three verbosity levels driven by character.preferences.combat_log_verbosity:
 *   - basic:    "You dealt 132 damage."
 *   - detailed: "You dealt 132 Fire damage."
 *   - advanced: full itemized breakdown with source attribution.
 *
 * A ⚙ button in the ribbon lets the player switch level. The choice is
 * persisted server-side via /api/character/preferences.
 *
 * Server-side breakdown data lives in `ch._last_damage_breakdown` — this
 * component reads only what it needs and never mutates the character.
 */
import { useState } from "react";
import { Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LEVELS = [
  { key: "basic",    label: "Basic"    },
  { key: "detailed", label: "Detailed" },
  { key: "advanced", label: "Advanced" },
];


function summarize(result, level) {
  if (!result) return null;
  const total = result.final_damage;
  if (level === "basic") return `→ ${total} damage`;

  // detailed: show element split
  const elParts = Object.entries(result.elemental || {})
    .filter(([, v]) => Number(v) > 0)
    .map(([el, v]) => `${v} ${el}`);
  const phys = result.physical > 0 ? [`${result.physical} Physical`] : [];
  const parts = [...phys, ...elParts];
  if (level === "detailed") {
    return parts.length ? `→ ${parts.join(" + ")} (total ${total})` : `→ ${total} damage`;
  }
  // advanced returns null so the caller renders the full breakdown
  return null;
}


export function CombatLogRibbon({ ch, setCh, entries }) {
  const [open, setOpen] = useState(false);
  const level = ch?.preferences?.combat_log_verbosity || "detailed";
  const advanced = level === "advanced";

  // Merge server damage breakdowns onto the last few combat log lines.
  // Log entries carry the timestamp; breakdowns are appended in insertion order.
  const combatEntries = (entries || [])
    .filter(e => e.kind === "combat" || e.kind === "combat_start")
    .slice(-6)
    .reverse();
  const breakdowns = (ch?._last_damage_breakdown || []).slice(-6).reverse();

  const setLevel = async (next) => {
    setOpen(false);
    // Optimistic update
    const optimistic = {
      ...ch,
      preferences: { ...(ch?.preferences || {}), combat_log_verbosity: next },
    };
    setCh(optimistic);
    try {
      await api.setPreferences({ combat_log_verbosity: next });
    } catch (e) {
      toast.error("Could not save log preference");
      setCh(ch);
    }
  };

  return (
    <div className="mx-4 mb-2 panel px-3 py-2 max-h-40 overflow-y-auto font-body text-xs text-[#B0ADA5] relative" data-testid="combat-log">
      {/* Verbosity switcher */}
      <div className="absolute top-1 right-1 flex items-center gap-1">
        <button
          onClick={() => setOpen(o => !o)}
          className="p-1 border border-[#333] text-[#8c8c8c] hover:text-[#E5C34A] hover:border-[#B89C3A] transition-colors"
          title="Combat log detail level"
          data-testid="combat-log-settings"
        >
          <Settings2 size={12} strokeWidth={1.8} />
        </button>
        {open && (
          <div className="absolute top-6 right-0 panel p-1 border border-[#B89C3A] bg-[#050505] z-20 flex flex-col min-w-[110px]"
               data-testid="combat-log-menu">
            {LEVELS.map(l => (
              <button
                key={l.key}
                onClick={() => setLevel(l.key)}
                data-testid={`combat-log-level-${l.key}`}
                className={`text-left px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors
                  ${l.key === level ? "text-[#E5C34A] bg-[#B89C3A]/10" : "text-[#8c8c8c] hover:text-[#B0ADA5]"}`}
              >
                {l.key === level ? "● " : "○ "}{l.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Log entries */}
      {combatEntries.length === 0 && (
        <div className="italic text-[#666] pr-8">Combat begins...</div>
      )}
      {combatEntries.map((e, i) => {
        const bd = breakdowns[i];
        const summary = bd?.result ? summarize(bd.result, level) : null;
        return (
          <div key={e.id} className="border-l border-[#262626] pl-2 py-1 pr-8 fade-up">
            <div>{e.text}</div>
            {summary && (
              <div className="font-mono text-[10px] text-[#B89C3A] tracking-wide mt-0.5">
                {summary}
              </div>
            )}
            {advanced && bd?.result?.breakdown && (
              <div className="mt-1 pl-2 border-l border-[#B89C3A]/40 space-y-0.5">
                {bd.result.breakdown.map((b, j) => (
                  <div key={j} className="font-mono text-[10px] text-[#8c8c8c]">
                    <span className="text-[#B0ADA5]">{b.label}</span>
                    <span className="text-[#666]"> → {typeof b.total === "number" ? b.total.toFixed(1) : b.total}</span>
                  </div>
                ))}
                <div className="font-mono text-[10px] text-[#E5C34A]">
                  Final: {bd.result.final_damage}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
